/**
 * Upsell Popup Functionality
 * Handles the display and interaction of the upsell popup similar to Huel's design
 */

class UpsellPopup {
    constructor() {
      this.popup = document.getElementById('upsell-popup');
      this.slider = document.getElementById('upsell-products-slider');
      this.loading = document.getElementById('upsell-loading');
      this.dots = document.getElementById('upsell-dots');
      this.prevButton = document.getElementById('upsell-prev');
      this.nextButton = document.getElementById('upsell-next');
      this.cartButton = document.getElementById('upsell-cart-button');
      this.shippingProgress = document.getElementById('upsell-shipping-progress');
      this.shippingText = document.getElementById('shipping-bar-text');
      
      this.currentSlide = 0;
      this.productsPerSlide = window.innerWidth <= 768 ? 1 : 2;
      this.totalSlides = 0;
      this.upsellProducts = [];
      this.cartData = null;
      
      this.init();
    }
  
    init() {
      this.bindEvents();
      this.setupShippingBar();
    }
  
    bindEvents() {
      // Close popup events
      this.popup?.querySelector('.upsell-popup__close')?.addEventListener('click', () => this.close());
      this.popup?.querySelector('.upsell-popup__overlay')?.addEventListener('click', () => this.close());
      
      // Navigation events
      this.prevButton?.addEventListener('click', () => this.previousSlide());
      this.nextButton?.addEventListener('click', () => this.nextSlide());
      
      // Cart button
      this.cartButton?.addEventListener('click', () => this.openCartDrawer());
      
      // Add to cart buttons (delegated event)
      this.popup?.addEventListener('click', (e) => {
        if (e.target.classList.contains('upsell-product__add-button')) {
          e.preventDefault();
          this.addUpsellToCart(e.target);
        }
      });
  
      // Listen for cart updates to update shipping bar
      document.addEventListener('cart:updated', (e) => {
        this.updateShippingBar(e.detail.cart);
      });
  
      // Handle window resize
      window.addEventListener('resize', () => {
        this.productsPerSlide = window.innerWidth <= 768 ? 1 : 2;
        this.updateSlider();
      });

      // Drag / swipe navigation on the products slider
      if (this.slider) {
        let isDragging = false;
        let dragStartX = 0;

        const getClientX = (event) => {
          if (event.type.startsWith('mouse')) {
            return event.pageX;
          }
          if (event.touches && event.touches.length > 0) {
            return event.touches[0].clientX;
          }
          if (event.changedTouches && event.changedTouches.length > 0) {
            return event.changedTouches[0].clientX;
          }
          return 0;
        };

        const handleDragStart = (event) => {
          isDragging = true;
          dragStartX = getClientX(event);
        };

        const handleDragEnd = (event) => {
          if (!isDragging) return;
          isDragging = false;

          const endX = getClientX(event);
          const deltaX = endX - dragStartX;
          const threshold = 50;

          if (deltaX > threshold) {
            this.previousSlide();
          } else if (deltaX < -threshold) {
            this.nextSlide();
          }
        };

        this.slider.addEventListener('mousedown', handleDragStart);
        this.slider.addEventListener('touchstart', handleDragStart, { passive: true });
        window.addEventListener('mouseup', handleDragEnd);
        window.addEventListener('touchend', handleDragEnd, { passive: true });
      }
  
      // Keyboard navigation
      document.addEventListener('keydown', (e) => {
        if (this.popup?.style.display !== 'none') {
          if (e.key === 'Escape') this.close();
          if (e.key === 'ArrowLeft') this.previousSlide();
          if (e.key === 'ArrowRight') this.nextSlide();
        }
      });
    }
  
    setupShippingBar() {
      // Get shipping thresholds from upsell config
      this.shippingThresholds = window.upsellConfig?.shippingTiers || {
        freeShipping: 5000, // 50€ in cents
        tier1: null,
        tier2: null,
        tier3: null,
        tier4: null,
        tier5: null,
        tier6: null
      };
  
      console.log('Shipping thresholds:', {
          freeShipping: this.shippingThresholds.freeShipping,
          tier1: this.shippingThresholds.freeShipping,
          tier2: this.shippingThresholds.tier2,
          tier3: this.shippingThresholds.tier3,
          tier4: this.shippingThresholds.tier4,
          tier5: this.shippingThresholds.tier5,
          tier6: this.shippingThresholds.tier6
      });
      
      // Get the maximum threshold for progress bar calculation
      this.maxThreshold = Math.max(
        this.shippingThresholds.freeShipping,
        this.shippingThresholds.tier1 || 0,
        this.shippingThresholds.tier2 || 0,
        this.shippingThresholds.tier3 || 0,
        this.shippingThresholds.tier4 || 0,
        this.shippingThresholds.tier5 || 0,
        this.shippingThresholds.tier6 || 0
      );
      
      // Get tier messages from upsell config
      this.tierMessages = window.upsellConfig?.tierMessages || {
        freeShipping: 'Dir fehlen nur noch {amount} bis zum Gratis-Versand!',
        tier1: 'Dir fehlen nur noch {amount} bis zum ersten Geschenk!',
        tier2: 'Dir fehlen nur noch {amount} bis zum zweiten Geschenk!',
        tier3: 'Dir fehlen nur noch {amount} bis zum dritten Geschenk!',
        tier4: 'Dir fehlen nur noch {amount} bis zum vierten Geschenk!',
        tier5: 'Dir fehlen nur noch {amount} bis zum fünften Geschenk!',
        tier6: 'Dir fehlen nur noch {amount} bis zum sechsten Geschenk!',
        maxReached: 'Alle Geschenke erreicht! 🎉'
      };
    }
  
    showLoading() {
      if (this.loading) {
        this.loading.style.display = 'flex';
      }
      if (this.slider) {
        this.slider.style.display = 'none';
      }
      // Hide navigation while loading
      const navigation = this.popup?.querySelector('.upsell-popup__navigation');
      if (navigation) {
        navigation.style.display = 'none';
      }
    }
  
    hideLoading() {
      if (this.loading) {
        this.loading.style.display = 'none';
      }
      if (this.slider) {
        this.slider.style.display = 'flex';
      }
      // Show navigation after loading
      const navigation = this.popup?.querySelector('.upsell-popup__navigation');
      if (navigation) {
        navigation.style.display = 'flex';
      }
    }
  
    show(cartData, addedProduct) {
      // Check if upsell popup is enabled
      if (!window.upsellConfig?.enabled) {
        this.openCartDrawer();
        return;
      }
      
      this.cartData = cartData;
      this.popup.style.display = 'flex';
      if (!document.body.classList.contains('old-popup')) {
        document.body.style.overflow = 'hidden';
      }
      
      // Update shipping bar
      this.updateShippingBar(cartData);
      
      // Load upsell products with delay
      const delay = window.upsellConfig?.delayBeforeShow || 500;
      setTimeout(() => {
        this.loadUpsellProducts(addedProduct);
      }, delay);
      
      // Focus management
      this.popup.focus();
    }
  
    close() {
      // Refresh the cart drawer
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart: this.cartData,
            openMiniCart: false, // Don't auto-open cart drawer
          },
        })
      );
      
      this.popup.style.display = 'none';
      document.body.style.overflow = 'auto';
      this.currentSlide = 0;
      // Reset loading state
      this.hideLoading();
    }
  
    async loadUpsellProducts(addedProduct) {
      
      // Show loading state
      this.showLoading();
      
      try {
        // Get recommended products based on the added product
        const recommendations = await this.getProductRecommendations(addedProduct);
        
        this.upsellProducts = recommendations;
        
        if (recommendations.length > 0) {
          this.renderProducts();
          this.updateNavigation();
          this.hideLoading();
        } else {
          console.warn('No recommendations to display');
          // Hide the products section if no recommendations
          const productsSection = this.popup.querySelector('.upsell-popup__products');
          if (productsSection) {
            productsSection.style.display = 'none';
          }
          this.hideLoading();
        }
      } catch (error) {
        console.error('Error loading upsell products:', error);
        // Hide the products section if no recommendations
        const productsSection = this.popup.querySelector('.upsell-popup__products');
        if (productsSection) {
          productsSection.style.display = 'none';
        }
        this.hideLoading();
      }
    }
  
    async getProductRecommendations(addedProduct) {
      const recommendations = [];
      
      // First try to get popular products as a reliable fallback
      try {
        const popularProducts = await this.getPopularProducts(addedProduct);
        recommendations.push(...popularProducts);
      } catch (error) {
        console.error('Error fetching popular products:', error);
      }
      
      // If we have a product handle, try to get related products
      if (addedProduct?.handle && recommendations.length > 0) {
        try {
          const response = await fetch(`/products/${addedProduct.handle}.js`);
          if (response.ok) {
            const product = await response.json();
            
            // Find related products (simplified logic)
            const relatedProducts = await this.findRelatedProducts(product);
            if (relatedProducts.length > 0) {
              // Replace some popular products with related ones
              const maxProducts = window.upsellConfig?.maxRecommendations || 4;
              const relatedCount = Math.min(relatedProducts.length, Math.floor(maxProducts / 2));
              recommendations.splice(0, relatedCount, ...relatedProducts.slice(0, relatedCount));
            }
          }
        } catch (error) {
          console.error('Error fetching product for recommendations:', error);
        }
      }
      
      // Ensure we have at least some products
      if (recommendations.length === 0) {
        console.warn('No recommendations found, using fallback');
        // Fallback to a simple collection fetch
        try {
          const response = await fetch('/collections/all/products.json?limit=4');
          const data = await response.json();
          recommendations.push(...data.products.filter(p => p.available));
        } catch (error) {
          console.error('Fallback recommendation fetch failed:', error);
        }
      }
      
      return recommendations;
    }
  
    async findRelatedProducts(product) {
      const relatedProducts = [];
      
      // Find products with similar tags
      if (product.tags && product.tags.length > 0) {
        try {
          // Try to find products with the same first tag
          const tag = product.tags[0];
          const response = await fetch(`/collections/all/products.json?limit=20`);
          const data = await response.json();
          
          const taggedProducts = data.products.filter(p => 
            p.id !== product.id && 
            p.tags && p.tags.includes(tag) && 
            p.available
          );
          
          relatedProducts.push(...taggedProducts);
          
          // If we don't have enough products, try with other tags
          if (relatedProducts.length < 2 && product.tags.length > 1) {
            for (let i = 1; i < product.tags.length && relatedProducts.length < 4; i++) {
              const additionalTag = product.tags[i];
              const additionalProducts = data.products.filter(p => 
                p.id !== product.id && 
                p.tags && p.tags.includes(additionalTag) && 
                p.available &&
                !relatedProducts.some(rp => rp.id === p.id)
              );
              relatedProducts.push(...additionalProducts.slice(0, 2));
            }
          }
        } catch (error) {
          console.error('Error finding related products:', error);
        }
      }
      
      return relatedProducts;
    }
  
    async getPopularProducts(addedProduct) {
      try {
        const productId = addedProduct.product_id;
        const intent = 'related';
        const response = await fetch(`/recommendations/products.json?product_id=${productId}&intent=${intent}`);
        const data = await response.json();
        return data.products.filter(p => p.available);
      } catch (error) {
        console.error('Error fetching popular products:', error);
        return [];
      }
    }
  
    renderProducts() {
      if (!this.slider) {
        console.error('Slider element not found');
        return;
      }
      
      if (this.upsellProducts.length === 0) {
        console.warn('No products to render');
        return;
      }
      
      this.slider.innerHTML = '';
      const template = document.getElementById('upsell-product-template');
      
      if (!template) {
        console.error('Product template not found, creating fallback');
        // Create a simple fallback without template
        this.upsellProducts.forEach((product, index) => {
          const productElement = this.createFallbackProductElement(product);
          this.slider.appendChild(productElement);
        });
      } else {
        this.upsellProducts.forEach((product, index) => {
          const productElement = this.createProductElement(product, template);
          this.slider.appendChild(productElement);
        });
      }
      this.totalSlides = this.slider.querySelectorAll('.upsell-product:not([style*="display: none"])').length;
      console.log('totalSlides:', this.totalSlides);
      this.updateSlider();
    }
  
    createProductElement(product, template) {
      const element = template.content.cloneNode(true);
      const productEl = element.querySelector('.upsell-product');
      if (product.title.includes('Bundle') || product.title.includes('Gratis')) {
        productEl.style.display = 'none';
      }
      
      if (!productEl) {
        console.error('Product element not found in template');
        return element;
      }
      
      // Set product data
      const img = productEl.querySelector('.upsell-product__img');
      const title = productEl.querySelector('.upsell-product__title');
      const priceCurrent = productEl.querySelector('.upsell-product__price-current');
      const priceCompare = productEl.querySelector('.upsell-product__price-compare');
      const addButton = productEl.querySelector('.upsell-product__add-button');
      
      // Product image
      if (product.featured_image) {
        img.src = product.featured_image;
        img.alt = product.title;
      } else {
        console.warn('No featured image for product:', product.title);
      }
      
      // Product title
      title.textContent = product.title.split('(')[0];
      
      // Product price
      const variant = product.variants?.[0];
      if (variant) {
        const currentPrice = this.formatPrice(variant.price);
        priceCurrent.textContent = currentPrice;
        
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          const comparePrice = this.formatPrice(variant.compare_at_price);
          priceCompare.textContent = comparePrice;
          priceCompare.style.display = 'inline';
        }
        
        addButton.dataset.variantId = variant.id;
      } else {
        console.warn('No variant found for product:', product.title);
      }
      
      // Product features (if available)
      const features = productEl.querySelector('.upsell-product__features');
      if (product.metafields?.custom?.features) {
        features.textContent = product.metafields.custom.features;
      } else if (product.tags && product.tags.length > 0) {
        features.textContent = product.tags.slice(0, 2).join(' • ');
      }
      
      return element;
    }
  
    createFallbackProductElement(product) {
      const div = document.createElement('div');
      div.className = 'upsell-product';
      
      const variant = product.variants?.[0];
      const price = variant ? this.formatPrice(variant.price) : 'N/A';
      
      div.innerHTML = `
  
        <a href="${product.url}" class="upsell-product__image">
          <img src="${product.featured_image || ''}" alt="${product.title}" class="upsell-product__img">
        </a>
        <div class="upsell-product__content">
          <h4 class="upsell-product__title">${product.title.split('(')[0]}</h4>
          <div class="upsell-product__details">
            <div class="upsell-product__price">
              <span class="upsell-product__price-current">${price}</span>
            </div>
            <div class="upsell-product__features">
              ${product.tags ? product.tags.slice(0, 2).join(' • ') : ''}
            </div>
          </div>
          <button type="button" class="upsell-product__add-button" data-variant-id="${variant?.id || ''}">
            In den Warenkorb
          </button>
        </div>
      `;
      
      return div;
    }
  
    formatPrice(priceInCents) {
      const price = priceInCents / 100;
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR'
      }).format(price);
    }
  
    updateSlider() {
      if (!this.slider) return;
      
      const slideWidth = this.slider.querySelector('.upsell-product:not([style*="display: none"])').offsetWidth;
      console.log('slideWidth:', slideWidth);
      const translateX = -this.currentSlide * (slideWidth + 20);
      this.slider.style.transform = `translateX(${translateX}px)`;
      
      // Update navigation buttons
      this.prevButton.disabled = this.currentSlide === 0;
      this.nextButton.disabled = this.currentSlide >= this.totalSlides - 1;
    }
  
    updateNavigation() {
      if (!this.dots) return;
      
      this.dots.innerHTML = '';
      
      for (let i = 0; i < this.totalSlides; i++) {
        const dot = document.createElement('div');
        dot.className = `upsell-popup__dot ${i === this.currentSlide ? 'active' : ''}`;
        dot.addEventListener('click', () => this.goToSlide(i));
        this.dots.appendChild(dot);
      }
    }
  
    previousSlide() {
      if (this.currentSlide > 0) {
        this.currentSlide--;
        this.updateSlider();
        this.updateDots();
      }
    }
  
    nextSlide() {
      if (this.currentSlide < this.totalSlides - 1) {
        this.currentSlide++;
        this.updateSlider();
        this.updateDots();
      }
    }
  
    goToSlide(index) {
      this.currentSlide = index;
      this.updateSlider();
      this.updateDots();
    }
  
    updateDots() {
      const dots = this.dots?.querySelectorAll('.upsell-popup__dot');
      dots?.forEach((dot, index) => {
        dot.classList.toggle('active', index === this.currentSlide);
      });
    }
  
    async addUpsellToCart(button) {
      const variantId = button.dataset.variantId;
      if (!variantId) return;
      
      button.classList.add('loading');
      button.disabled = true;
      
      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', 1);
        formData.append('sections', 'mini-cart');
        
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.description || 'Failed to add product to cart.');
        }
        
        const responseJson = await response.json();
        
        // Dispatch variant:added event
        document.dispatchEvent(
          new CustomEvent('variant:added', {
            bubbles: true,
            detail: {
              variant: responseJson.hasOwnProperty('items') ? responseJson.items[0] : responseJson,
            },
          })
        );
        
        // Fetch updated cart data
        const cartResponse = await fetch('/cart.js');
        const cartContent = await cartResponse.json();
        
        // Dispatch cart:updated event
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:updated', {
            bubbles: true,
            detail: {
              cart: cartContent,
            },
          })
        );
        
        // Add mini-cart section to cart content
        cartContent.sections = responseJson.sections;
        
        // Update our cart data
        this.cartData = cartContent;
        this.updateShippingBar(cartContent);
        
        // Show success feedback
        button.textContent = 'Hinzugefügt!';
        button.classList.remove('loading');
        button.disabled = false;
        setTimeout(() => {
          button.textContent = 'In den Warenkorb'; 
        }, 2000);
        
      } catch (error) {
        console.error('Error adding upsell to cart:', error);
        button.textContent = 'Fehler';
        setTimeout(() => {
          button.textContent = 'In den Warenkorb';
          button.classList.remove('loading');
          button.disabled = false;
        }, 2000);
      }
    }
  
    updateShippingBar(cartData) {
      if (!this.shippingProgress || !this.shippingText) return;
      
      const totalPrice = cartData.total_price / 100 || 0;
      const progress = Math.min(totalPrice / this.maxThreshold, 1);
  
      
      // Update progress bar
      this.shippingProgress.style.setProperty('--progress', progress);
      
      // Update marker positions and active states
      this.updateShippingMarkers(totalPrice);
      
      // Update text based on current tier
      this.updateShippingText(totalPrice);
    }
    
    updateShippingMarkers(totalPrice) {
      // Update free shipping marker
      const shippingLine = this.shippingProgress.querySelector('#shipping-line');
      if (shippingLine) {
        const position = (this.shippingThresholds.freeShipping / this.maxThreshold) * 100;
        shippingLine.style.left = `${position}%`;
        shippingLine.classList.toggle('active', totalPrice >= this.shippingThresholds.freeShipping);
      }
      
      // Update gift tier markers
      for (let i = 1; i <= 6; i++) {
        const tierKey = `tier${i}`;
        const threshold = this.shippingThresholds[tierKey];
        const giftLine = this.shippingProgress.querySelector(`#gift-line-${i}`);
        
        if (threshold && giftLine) {
          const position = (threshold / this.maxThreshold) * 100;
          giftLine.style.left = `${position}%`;
          giftLine.classList.toggle('active', totalPrice >= threshold);
        }
      }
    }
    
    updateShippingText(totalPrice) {
      let message = '';
      let remainingAmount = 0;
      
      // Determine which tier message to show
      if (totalPrice < this.shippingThresholds.freeShipping) {
        remainingAmount = this.shippingThresholds.freeShipping - totalPrice;
        message = this.tierMessages.freeShipping;
      } else if (this.shippingThresholds.tier1 && totalPrice < this.shippingThresholds.tier1) {
        remainingAmount = this.shippingThresholds.tier1 - totalPrice;
        message = this.tierMessages.tier1;
      } else if (this.shippingThresholds.tier2 && totalPrice < this.shippingThresholds.tier2) {
        remainingAmount = this.shippingThresholds.tier2 - totalPrice;
        message = this.tierMessages.tier2;
      } else if (this.shippingThresholds.tier3 && totalPrice < this.shippingThresholds.tier3) {
        remainingAmount = this.shippingThresholds.tier3 - totalPrice;
        message = this.tierMessages.tier3;
      } else if (this.shippingThresholds.tier4 && totalPrice < this.shippingThresholds.tier4) {
        remainingAmount = this.shippingThresholds.tier4 - totalPrice;
        message = this.tierMessages.tier4;
      } else if (this.shippingThresholds.tier5 && totalPrice < this.shippingThresholds.tier5) {
        remainingAmount = this.shippingThresholds.tier5 - totalPrice;
        message = this.tierMessages.tier5;
      } else if (this.shippingThresholds.tier6 && totalPrice < this.shippingThresholds.tier6) {
        remainingAmount = this.shippingThresholds.tier6 - totalPrice;
        message = this.tierMessages.tier6;
      } else {
        message = this.tierMessages.maxReached;
      }
  
      console.log('remainingAmount:', remainingAmount);
      
      // Replace {amount} placeholder with formatted amount
      if (remainingAmount > 0) {
        const formattedAmount = this.formatPrice(remainingAmount * 100);
        message = message.replace('[amount]', formattedAmount);
      }
      
      this.shippingText.innerHTML = message;
    }
  
    openCartDrawer() {
      // Dispatch cart:refresh event to open cart drawer
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart: this.cartData,
            openMiniCart: true,
          },
        })
      );
      
      this.close();
    }
  }
  
  // Initialize the upsell popup when DOM is ready
  document.addEventListener('DOMContentLoaded', () => {
    window.upsellPopup = new UpsellPopup();
  });
  
  // Export for use in other scripts
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = UpsellPopup;
  }
  